<script>
import {
  notificationComputed,
  notificationMethods,
} from '@state/helpers/notification'
import { agentDetailsMethods } from '@state/helpers/agent-details'
import BasicInfo from '@views/Notification/form/basic-info.vue'
import DeliveryPeriod from '@views/Notification/form/delivery-period.vue'
import DeliveryTarget from '@views/Notification/form/delivery-target.vue'
import Preview from '@views/Notification/form/preview.vue'

import {
  PLACE,
  NOTIFICATION_STATUS_NAME,
  STATUS_REQUEST,
  DELIVERED,
} from '@utils/constant'
import store from '@state/store'

export default {
  page: {
    title: 'Notification detail',
    meta: [{ name: 'description', content: 'The Notification detail page.' }],
  },
  components: {
    BasicInfo,
    DeliveryPeriod,
    DeliveryTarget,
    Preview,
  },
  data() {
    return {
      currentUser: store.getters['auth/getCurrentUser'],
      cms: false,
      basic: {},
      previewErrors: null,
      period: {},
      target: {},
      delivered: '0',
      previewed: false,
      openConfirm: false,
      triggerMask: false,
      confirmType: '',
    }
  },
  computed: {
    ...notificationComputed,
    previewComputed() {
      return !this.previewed
    },
    waitingForDelivery() {
      return (
        this.getNotificationDetail?.status ===
        NOTIFICATION_STATUS_NAME.WAITING_FOR_DELIVERY
      )
    },
    deliveredComputed() {
      return (
        this.getNotificationDetail?.status === NOTIFICATION_STATUS_NAME.DELIVERY
      )
    },
    endOrDisabled() {
      return [
        NOTIFICATION_STATUS_NAME.END_OF_DELIVERY,
        NOTIFICATION_STATUS_NAME.DISABLED,
      ].includes(this.getNotificationDetail?.status)
    },
    applicationForInvalidation() {
      return (
        this.getNotificationDetail?.status ===
        NOTIFICATION_STATUS_NAME.APPLICATION_FOR_INVALIDATION
      )
    },
  },

  watch: {
    getNotificationDetail() {
      this.cms = this.getNotificationDetail?.place === PLACE.APP
      this.delivered = this.getNotificationDetail?.filter_obj
        ? DELIVERED.OFF
        : DELIVERED.ON
      if (this.getNotificationDetail?.agent_id) {
        this.getDetailsById({ id: this.getNotificationDetail.agent_id })
      }
    },
    getDeleteStatusRequest() {
      if (this.getDeleteStatusRequest === STATUS_REQUEST.SUCCESS) {
        this.$router.push({ name: 'notificationList' })
      }
    },
    getDeliverStatusRequest() {
      if (this.getDeliverStatusRequest === STATUS_REQUEST.SUCCESS) {
        this.resetAfterStop()
        this.fetchingNotificationById({ id: this.getNotificationDetail?.id })
      }
    },
    getAcceptRevokeStatusRequest() {
      if (this.getAcceptRevokeStatusRequest === STATUS_REQUEST.SUCCESS) {
        this.resetAfterAccept()
        this.fetchingNotificationById({ id: this.getNotificationDetail?.id })
      }
    },
    getRejectRevokeStatusRequest() {
      if (this.getRejectRevokeStatusRequest === STATUS_REQUEST.SUCCESS) {
        this.resetAfterReject()
        this.fetchingNotificationById({ id: this.getNotificationDetail?.id })
      }
    },
  },
  mounted() {
    const { id } = this.$route.params
    this.fetchingNotificationById({ id: parseInt(id, 10) })
  },
  destroyed() {
    this.resetAgentDetails()
    this.resetDetails()
  },
  methods: {
    ...notificationMethods,
    ...agentDetailsMethods,
    handleBasicInfoData(data) {
      this.basic = data
    },
    handlePeriodData(data) {
      this.period = data
    },
    handleTargetData(data) {
      this.target = data
    },
    handleValidatePreview(error) {
      this.previewErrors = error
    },
    handleDelivered(value) {
      this.delivered = value
    },
    handleEditNotification() {
      const basic = {
        ...this.basic,
        place: parseInt(this.basic.place),
        ...(this.basic.image_binary && {
          image_binary: this.basic.image_binary.split(',')[1],
        }),
      }
      basic.id = parseInt(this.$route.params.id, 10)
      let target = null
      if (this.target !== null && this.delivered !== DELIVERED.ON) {
        target = { ...this.target }
        const keys = [
          'age_range_lower',
          'age_range_upper',
          'weight_differ_lower',
          'weight_differ_upper',
          'body_fat_rate_differ_lower',
          'body_fat_rate_differ_upper',
        ]
        for (const key of keys) {
          if (!target[key] && target[key] !== 0) {
            target[key] = -9999
          }
        }
      } else {
        target = null
      }

      this.editNotification({
        ...basic,
        ...this.period,
        filter_obj: target ? { ...target } : null,
        ...{
          updated_admin_id: this.currentUser?.id,
        },
      }).then(() => {
        const { id } = this.$route.params
        this.fetchingNotificationById({ id: parseInt(id, 10) })
      })
    },
    handlePreview(pre) {
      this.previewed = pre
    },
    handleTriggerMask(value) {
      this.triggerMask = value
    },
    handleActionByStatus(status) {
      this.openConfirm = !this.openConfirm
      this.confirmType = status
    },
    submit() {
      this.openConfirm = !this.openConfirm
      const { id } = this.getNotificationDetail
      switch (this.confirmType) {
        case 'delete':
          this.deleteNotification({ id })
          break
        case 'deliver_end':
          this.deliverEndNotification({ id })
          break
        case 'accept':
          this.acceptRevoke({ id })
          break
        case 'reject':
          this.rejectRevoke({ id })
          break
      }
    },
  },
}
</script>

<template>
  <div id="notification-edit">
    <CRow>
      <CCol sm="6">
        <BasicInfo
          variant="edit"
          :trigger-mask="triggerMask"
          @enable:edit="handleTriggerMask"
          @change:data="handleBasicInfoData"
          @edit:notification="handleEditNotification"
        />
        <DeliveryPeriod
          variant="edit"
          :trigger-mask="triggerMask"
          @enable:edit="handleTriggerMask"
          @change:data="handlePeriodData"
          @edit:notification="handleEditNotification"
        />
        <DeliveryTarget
          v-if="cms"
          variant="edit"
          :trigger-mask="triggerMask"
          @enable:edit="handleTriggerMask"
          @change:data="handleTargetData"
          @change:delivered="handleDelivered"
          @edit:notification="handleEditNotification"
        />
      </CCol>
      <CCol sm="6">
        <Preview
          variant="edit"
          :basic="basic"
          :period="period"
          :target="target"
          :delivered="delivered"
          :trigger-mask="triggerMask"
          @preview:update="handlePreview"
          @validate="handleValidatePreview"
        />
        <div class="d-flex justify-content-end">
          <CButton
            v-if="applicationForInvalidation"
            color="dark"
            variant="outline"
            @click="handleActionByStatus('accept')"
          >
            承認
          </CButton>
          <CButton
            v-if="applicationForInvalidation"
            color="dark"
            variant="outline"
            @click="handleActionByStatus('reject')"
          >
            否承認
          </CButton>
          <CButton
            v-if="deliveredComputed"
            color="dark"
            variant="outline"
            @click="handleActionByStatus('deliver_end')"
          >
            配信終了
          </CButton>
          <CButton
            v-if="waitingForDelivery || endOrDisabled"
            color="dark"
            variant="outline"
            @click="handleActionByStatus('delete')"
          >
            削除
          </CButton>
        </div>
      </CCol>
    </CRow>
    <BaseModalConfirm
      backdrop-close
      :show.sync="openConfirm"
      :type="confirmType"
      @closeModal="openConfirm = !openConfirm"
      @submit="submit"
    />
  </div>
</template>
